import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useRecords } from '../../hooks/useRecords';
import { formStyles } from '../../styles/formStyles';
import { tableStyles } from '../../styles/tableStyles';
import { formatDate, formatMoney, API_URL } from '../../utils/constants';
import EditRecordModal from '../EditRecordModal';
import RecordInputForm from './RecordInputForm';
import SalesReport from './SalesReport';

// 날짜 변환 함수 (YYMMDD → YYYY-MM-DD)
const parseSimpleDate = (input) => {
  if (!input || input.length !== 6) return '';
  const yy = input.substring(0, 2);
  const mm = input.substring(2, 4);
  const dd = input.substring(4, 6);
  return `20${yy}-${mm}-${dd}`;
};

function Dashboard({ token }) {
  const { records, totalCount, searchTerm, setSearchTerm, refresh } = useRecords(token);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // 탭 상태
  const [activeTab, setActiveTab] = useState('list'); // 'list' 또는 'sales'
  
  // 매입 데이터 (한 번만 불러오기)
  const [materials, setMaterials] = useState([]);
  const [materialsLoaded, setMaterialsLoaded] = useState(false);
  
  // 매입 데이터 조회 (한 번만)
  useEffect(() => {
    if (token && !materialsLoaded) {
      const fetchMaterials = async () => {
        try {
          const response = await fetch(`${API_URL}/materials`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            setMaterials(data);
            setMaterialsLoaded(true);
          }
        } catch (error) {
          console.error('자재 조회 오류:', error);
        }
      };
      fetchMaterials();
    }
  }, [token, materialsLoaded]);
  
  // 추가 필터 상태 - 배열로 변경 (기본값: 모든 상태 선택)
  const [statusFilter, setStatusFilter] = useState(['진행', '완결', '미수', '취소', '대기']);
  const [paymentFilter, setPaymentFilter] = useState(['발행', '현금', '카드', '']); // 영수증 필터
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  
  // 정렬 상태
  const [sortField, setSortField] = useState('receipt_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // 무한 스크롤을 위한 상태
  const [displayCount, setDisplayCount] = useState(50);
  const observerTarget = useRef(null);
  const tableWrapperRef = useRef(null);

  // 체크박스 토글 함수
  const handleStatusToggle = (status) => {
    setStatusFilter(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    setDisplayCount(50); // 필터 변경 시 초기화
  };

  // 영수증 필터 토글 함수
  const handlePaymentToggle = (payment) => {
    setPaymentFilter(prev => 
      prev.includes(payment) 
        ? prev.filter(p => p !== payment)
        : [...prev, payment]
    );
    setDisplayCount(50); // 필터 변경 시 초기화
  };

  // 전체 선택/해제 토글
  const handleAllStatusToggle = () => {
    const allStatuses = ['진행', '완결', '미수', '취소', '대기'];
    // 모두 선택되어 있으면 → 전체 해제, 아니면 → 전체 선택
    if (statusFilter.length === allStatuses.length) {
      setStatusFilter([]);
    } else {
      setStatusFilter(allStatuses);
    }
    setDisplayCount(50); // 필터 변경 시 초기화
  };

  // 영수증 전체 선택/해제 토글
  const handleAllPaymentToggle = () => {
    const allPayments = ['발행', '현금', '카드', ''];
    if (paymentFilter.length === allPayments.length) {
      setPaymentFilter([]);
    } else {
      setPaymentFilter(allPayments);
    }
    setDisplayCount(50); // 필터 변경 시 초기화
  };

  // 날짜 입력 핸들러
  const handleStartDateChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setStartDateInput(value);
    if (value.length === 6) {
      setStartDate(parseSimpleDate(value));
    } else {
      setStartDate('');
    }
    setDisplayCount(50); // 필터 변경 시 초기화
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setEndDateInput(value);
    if (value.length === 6) {
      setEndDate(parseSimpleDate(value));
    } else {
      setEndDate('');
    }
    setDisplayCount(50); // 필터 변경 시 초기화
  };

  // 검색어 변경 시 초기화
  useEffect(() => {
    setDisplayCount(50);
  }, [searchTerm]);

  // 정렬 핸들러
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setDisplayCount(50);
  };

  // 필터링된 전체 레코드
  const allFilteredRecords = useMemo(() => {
    // 상태 필터가 비어있으면 빈 배열 반환 (아무것도 표시 안 함)
    if (statusFilter.length === 0) {
      return [];
    }

    let filtered = records;

    // 검색어 필터 (업체명, 담당자, 전화번호)
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        (r.company_name && r.company_name.toLowerCase().includes(lowerSearch)) ||
        (r.manager && r.manager.toLowerCase().includes(lowerSearch)) ||
        (r.phone && r.phone.includes(searchTerm)) ||
        (r.mobile && r.mobile.includes(searchTerm))
      );
    }

    // 상태 필터
    if (statusFilter.length < 5) {
      filtered = filtered.filter(r => statusFilter.includes(r.progress_status));
    }

    // 영수증 필터 (tax_invoice 필드 사용)
    const allPaymentOptions = ['발행', '현금', '카드', ''];
    // 전체 선택(4개 모두 선택)이 아닐 때만 필터링 수행
    // 즉, 1개~3개 선택 시에는 선택된 항목만 보여줌 (공란 포함 가능)
    if (paymentFilter.length < allPaymentOptions.length) {
      filtered = filtered.filter(r => {
        const taxInvoice = r.tax_invoice || '';
        return paymentFilter.includes(taxInvoice);
      });
    }

    // 날짜 필터
    if (startDate) {
      filtered = filtered.filter(r => r.receipt_date >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(r => r.receipt_date <= endDate);
    }

    // 정렬
    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // null/undefined 처리
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // 숫자 비교
      if (sortField === 'total_cost') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      // 비교
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [records, statusFilter, paymentFilter, startDate, endDate, searchTerm, sortField, sortDirection]);

  // 실제 표시할 레코드 (무한 스크롤)
  const displayedRecords = useMemo(() => {
    return allFilteredRecords.slice(0, displayCount);
  }, [allFilteredRecords, displayCount]);

  // 더 로드할 데이터가 있는지 확인
  const hasMore = displayedRecords.length < allFilteredRecords.length;

  // Intersection Observer 콜백
  const loadMore = useCallback(() => {
    if (hasMore) {
      setDisplayCount(prev => prev + 50);
    }
  }, [hasMore]);

  // Intersection Observer 설정
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loadMore, hasMore]);

  return (
    <div style={formStyles.dashboardWrapper}>
      {/* 좌측: 입력 폼 (독립 스크롤) */}
      <RecordInputForm token={token} onSaveSuccess={refresh} />

      {/* 우측: 목록 영역 (독립 스크롤) */}
      <section style={tableStyles.listContainer}>
        {/* 탭 메뉴 */}
        <div style={{ 
          marginBottom: '15px', 
          display: 'flex', 
          gap: '10px', 
          borderBottom: '2px solid #e0e0e0' 
        }}>
          <button
            onClick={() => setActiveTab('list')}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              fontWeight: 'bold',
              border: 'none',
              backgroundColor: 'transparent',
              color: activeTab === 'list' ? '#3498db' : '#7f8c8d',
              borderBottom: activeTab === 'list' ? '3px solid #3498db' : '3px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
          >
            작업 목록
          </button>
          <button
            onClick={() => setActiveTab('sales')}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              fontWeight: 'bold',
              border: 'none',
              backgroundColor: 'transparent',
              color: activeTab === 'sales' ? '#3498db' : '#7f8c8d',
              borderBottom: activeTab === 'sales' ? '3px solid #3498db' : '3px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
          >
            매입/매출
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        {activeTab === 'sales' ? (
          <SalesReport token={token} materials={materials} records={records} />
        ) : (
          <>
            {/* 작업 목록 타이틀 */}
            <div style={{ marginBottom: '15px', paddingBottom: '8px', borderBottom: '2px solid #3498db' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#2c3e50' }}>작업 목록</h2>
            </div>

        {/* 필터 영역 - 한 줄에 모두 배치 */}
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px', 
          marginBottom: '15px',
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <input 
            type="text" 
            placeholder="업체명, 담당자, 전화번호" 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            style={{ ...tableStyles.searchInput, flex: '1 1 200px', minWidth: '200px' }}
          />
          <input 
            type="text" 
            value={startDateInput}
            onChange={handleStartDateChange}
            placeholder="YYMMDD"
            style={{ ...tableStyles.dateInput, width: '90px' }}
            maxLength="6"
          />
          <span style={{ margin: '0 5px' }}>~</span>
          <input 
            type="text" 
            value={endDateInput}
            onChange={handleEndDateChange}
            placeholder="YYMMDD"
            style={{ ...tableStyles.dateInput, width: '90px' }}
            maxLength="6"
          />
          
          {/* 체크박스 그룹 */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            alignItems: 'center',
            padding: '5px 10px',
            backgroundColor: 'white',
            borderRadius: '4px',
            border: '1px solid #ddd',
            flexWrap: 'wrap'
          }}>
            {['진행', '완결', '미수', '취소', '대기'].map(status => (
              <label key={status} style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}>
                <input 
                  type="checkbox" 
                  checked={statusFilter.includes(status)}
                  onChange={() => handleStatusToggle(status)}
                  style={{ cursor: 'pointer' }}
                />
                {status}
              </label>
            ))}
            <button 
              onClick={handleAllStatusToggle}
              style={{ 
                marginLeft: '5px',
                padding: '2px 8px',
                fontSize: '12px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer'
              }}
            >
              전체
            </button>
          </div>

          {/* 영수증 체크박스 그룹 */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            alignItems: 'center',
            padding: '5px 10px',
            backgroundColor: 'white',
            borderRadius: '4px',
            border: '1px solid #ddd',
            flexWrap: 'wrap'
          }}>
            {[
              { value: '발행', label: '발행' },
              { value: '현금', label: '현금' },
              { value: '카드', label: '카드' },
              { value: '', label: '공란' }
            ].map(payment => (
              <label key={payment.value} style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}>
                <input 
                  type="checkbox" 
                  checked={paymentFilter.includes(payment.value)}
                  onChange={() => handlePaymentToggle(payment.value)}
                  style={{ cursor: 'pointer' }}
                />
                {payment.label}
              </label>
            ))}
            <button 
              onClick={handleAllPaymentToggle}
              style={{ 
                marginLeft: '5px',
                padding: '2px 8px',
                fontSize: '12px',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer'
              }}
            >
              전체
            </button>
          </div>

          <button 
            onClick={() => { 
              setSearchTerm(''); 
              setStartDate(''); 
              setEndDate(''); 
              setStartDateInput('');
              setEndDateInput('');
              setStatusFilter(['진행', '완결', '미수', '취소', '대기']); 
              setPaymentFilter(['발행', '현금', '카드', '']);
              setSortField('receipt_date');
              setSortDirection('desc');
              setDisplayCount(50);
              // 스크롤 맨 위로
              if (tableWrapperRef.current) {
                tableWrapperRef.current.scrollTop = 0;
              }
            }}
            style={tableStyles.resetButton}
          >
            초기화
          </button>
          <div style={{ display: 'flex', gap: '15px', fontSize: '14px', color: '#2c3e50', marginLeft: 'auto', whiteSpace: 'nowrap', alignItems: 'center' }}>
            <span>전체: <strong>{totalCount}</strong>건</span>
            <span>필터: <strong>{allFilteredRecords.length}</strong>건</span>
            {(startDate && endDate) && (
              <span style={{ color: '#27ae60', fontWeight: 'bold' }}>
                합계: {formatMoney(allFilteredRecords.reduce((sum, r) => {
  const cost = parseFloat(r.total_cost) || 0;
  return sum + (r.vat_included ? Math.floor(cost * 1.1) : cost);
}, 0))}
              </span>
            )}
          </div>
        </div>

        {/* 테이블 영역 (독립 스크롤) */}
        <div ref={tableWrapperRef} style={tableStyles.tableWrapper}>
          <table style={tableStyles.responsiveTable}>
            <thead>
              <tr>
                <th 
                  style={{ ...tableStyles.tableHeader, width: '100px', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('receipt_date')}
                >
                  접수일 {sortField === 'receipt_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th 
                  style={{ ...tableStyles.tableHeader, width: '150px', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('company_name')}
                >
                  업체명 {sortField === 'company_name' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th 
                  style={{ ...tableStyles.tableHeader, width: '100px', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('manager')}
                >
                  담당자 {sortField === 'manager' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th style={{ ...tableStyles.tableHeader, width: '120px' }}>전화번호</th>
                <th style={tableStyles.tableHeader}>접수내역</th>
                <th style={tableStyles.tableHeader}>처리내역</th>
                <th 
                  style={{ ...tableStyles.tableHeader, width: '100px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('total_cost')}
                >
                  전체비용 {sortField === 'total_cost' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th 
                  style={{ ...tableStyles.tableHeader, width: '80px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('progress_status')}
                >
                  진행상태 {sortField === 'progress_status' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th 
                  style={{ ...tableStyles.tableHeader, width: '50px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('tax_invoice')}
                >
                  영수증 {sortField === 'tax_invoice' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th 
                  style={{ ...tableStyles.tableHeader, width: '100px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('completion_date')}
                >
                  완료일 {sortField === 'completion_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedRecords.map((record) => (
                <tr 
                  key={record.id} 
                  style={tableStyles.tableRow} 
                  onClick={() => { setSelectedRecord(record); setIsModalOpen(true); }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <td style={tableStyles.tableCell}>{formatDate(record.receipt_date)}</td>
                  <td style={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>{record.company_name}</td>
                  <td style={tableStyles.tableCell}>{record.manager || '-'}</td>
                  <td style={tableStyles.tableCell}>{record.phone || record.mobile || '-'}</td>
                  <td style={{ ...tableStyles.tableCell, ...tableStyles.ellipsis, maxWidth: '200px' }}>{record.receipt_details}</td>
                  <td style={{ ...tableStyles.tableCell, ...tableStyles.ellipsis, maxWidth: '200px' }}>{record.process_details || '-'}</td>
                 <td style={{ ...tableStyles.tableCell, textAlign: 'right' }}>
  {record.total_cost ? formatMoney(record.vat_included ? Math.floor(parseFloat(record.total_cost) * 1.1) : record.total_cost) : '-'}
</td>
                  <td style={{ ...tableStyles.tableCell, textAlign: 'center' }}>
                    <span style={tableStyles.statusBadge(record.progress_status)}>{record.progress_status}</span>
                  </td>
                  <td style={{ ...tableStyles.tableCell, textAlign: 'center' }}>
                    {record.tax_invoice || '-'}
                  </td>
                  <td style={{ ...tableStyles.tableCell, textAlign: 'center' }}>
                    {record.completion_date ? formatDate(record.completion_date) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Intersection Observer 타겟 & 로딩 인디케이터 */}
          <div 
            ref={observerTarget}
            style={{ 
              padding: '20px', 
              textAlign: 'center',
              color: '#7f8c8d',
              fontSize: '14px'
            }}
          >
            {hasMore ? '스크롤하여 더 보기...' : allFilteredRecords.length > 0 ? '모든 데이터를 표시했습니다.' : ''}
          </div>
        </div>
          </>
        )}
      </section>

      {isModalOpen && selectedRecord && (
        <EditRecordModal record={selectedRecord} token={token} onClose={() => { setIsModalOpen(false); refresh(); }} />
      )}
    </div>
  );
}

export default Dashboard;
